// ============================================================
// TMR Portal Admin — uma empresa fora de sincronia, com os dois lados
// ============================================================
import { Link } from 'react-router-dom';
import { ApiError } from '@tmr/api-client';
import { Btn } from '../../components/primitives';
import { Notice } from '../../components/Notice';
import { Skeleton } from '../../components/states';
import { Icons, StrokeIcon } from '../../lib/icons';
import { maskCNPJ } from '../../lib/format';
import { bandLabel, formatRateValue } from '../../data/rateFormat';
import { useCompanyRates, useRateDrift, useSyncRates } from '../../data/rates/queries';
import { rateTitle } from '../../data/rates/status';
import { disagreementSummary, isDiverging } from '../../data/ratePortfolio/status';
import type { RatesOutOfSyncItem } from '../../data/ratePortfolio/contract';

/** Props for {@link CompanyDriftRow}. */
interface CompanyDriftRowProps {
  item: RatesOutOfSyncItem;
  expanded: boolean;
  /** Whether the operator may reconcile this one Empresa (`company.rates.manage`). */
  canReconcile: boolean;
  onToggle: () => void;
}

/**
 * One Empresa in the portfolio view, and — when opened — the rate-by-rate comparison behind its counts.
 *
 * The detail is read on demand rather than with the list: it is two round trips per Empresa, one of them to
 * the legacy, and the list exists precisely so nobody has to make those for every Empresa to find the few
 * that matter.
 */
export function CompanyDriftRow({ item, expanded, canReconcile, onToggle }: CompanyDriftRowProps) {
  const drift = useRateDrift(item.companyId, expanded);
  // The drift response carries values but not rate types, and the unit of a rate comes from its type
  // (15 §5). Reading the Empresa's rates alongside is what keeps a Taxa de Saque from being labelled "%".
  const rates = useCompanyRates(expanded ? item.companyId : undefined);
  const sync = useSyncRates(item.companyId);

  const rateTypeById = new Map((rates.data?.items ?? []).map((rate) => [rate.id, rate]));
  const diverging = isDiverging(item);

  return (
    <div style={{ borderBottom: '1px solid var(--border-subtle)' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 16,
          padding: '14px 18px',
          flexWrap: 'wrap',
        }}
      >
        <div style={{ minWidth: 240, flex: '1 1 340px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 14.5, fontWeight: 550 }}>{item.companyName}</span>
            <span style={{ fontSize: 12, color: 'var(--fg-subtle)', fontFamily: 'var(--font-mono)' }}>
              {maskCNPJ(item.cnpj)}
            </span>
          </div>

          {/* Coloured by whether the legacy moved underneath us. An Empresa whose only count is
              awaitingExport is our own outbox still working — listed, but not an alarm. */}
          <div
            style={{
              fontSize: 13,
              color: diverging ? 'var(--danger)' : 'var(--fg-muted)',
              marginTop: 4,
            }}
          >
            {diverging && <StrokeIcon d={Icons.alert} size={13} />} {disagreementSummary(item)}
          </div>

          <div style={{ fontSize: 12.5, color: 'var(--fg-subtle)', marginTop: 4 }}>
            {item.checked === 1 ? '1 taxa comparada' : `${item.checked} taxas comparadas`}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <Btn variant="ghost" icon={expanded ? Icons.chevronD : Icons.chevronR} onClick={onToggle}>
            {expanded ? 'Fechar' : 'Ver os dois lados'}
          </Btn>

          <Link to={`/empresas/${item.companyId}`} style={{ textDecoration: 'none' }}>
            <Btn variant="ghost" icon={Icons.external}>Abrir empresa</Btn>
          </Link>

          {canReconcile && (
            <Btn
              variant="secondary"
              icon={Icons.swap}
              onClick={() => void sync.mutateAsync(undefined)}
              disabled={sync.isPending}
            >
              {sync.isPending ? 'Reconciliando…' : 'Reconciliar'}
            </Btn>
          )}
        </div>
      </div>

      {sync.error && (
        <div style={{ padding: '0 18px 14px' }}>
          <Notice tone="danger">
            {sync.error instanceof ApiError ? sync.error.detail : 'Não foi possível reconciliar esta empresa.'}
          </Notice>
        </div>
      )}

      {expanded && (
        <div style={{ padding: '0 18px 16px' }}>
          {drift.isPending || rates.isPending ? (
            <Skeleton rows={2} />
          ) : drift.isError ? (
            <Notice tone="danger">Não foi possível ler o sistema legado para esta empresa.</Notice>
          ) : !drift.data.legacyAvailable ? (
            <Notice tone="warning">
              O sistema legado não respondeu agora, então não dá para mostrar o outro lado.
            </Notice>
          ) : drift.data.items.length === 0 ? (
            <Notice tone="neutral">
              As {drift.data.checked} taxas exportadas conferem com o legado. O que esta empresa tem
              pendente é exportação, não divergência.
            </Notice>
          ) : (
            <div style={{ display: 'grid', gap: 8 }}>
              {drift.data.items.map((entry) => {
                const rate = rateTypeById.get(entry.rateId);

                return (
                  <div
                    key={entry.rateId}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 12,
                      padding: '10px 12px',
                      border: '1px solid var(--border-default)',
                      borderRadius: 'var(--radius-sm)',
                      flexWrap: 'wrap',
                    }}
                  >
                    <div style={{ fontSize: 13, minWidth: 200, flex: '1 1 240px' }}>
                      {rate ? `${rateTitle(rate)} · ${bandLabel(rate)}` : `Taxa ${entry.legacyRateId}`}
                    </div>

                    <div style={{ display: 'flex', gap: 18, alignItems: 'baseline' }}>
                      <Side
                        label="aqui"
                        value={rate ? formatRateValue(entry.ownValue, rate.rateTypeId) : String(entry.ownValue)}
                      />
                      <Side
                        label="no legado"
                        value={
                          entry.kind === 'Missing' || entry.legacyValue === null
                            ? 'linha ausente'
                            : rate
                              ? formatRateValue(entry.legacyValue, rate.rateTypeId)
                              : String(entry.legacyValue)
                        }
                        danger
                      />
                    </div>
                  </div>
                );
              })}

              {/* The number that is charged is the legacy's, whatever this screen says. */}
              <div style={{ fontSize: 12.5, color: 'var(--fg-muted)' }}>
                É o valor do legado que a clínica paga. Reconciliar traz o valor de lá para cá; para
                sobrescrever o legado, edite a taxa na aba da empresa.
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** One side of the comparison — the label under the number, so the two read as a pair. */
function Side({ label, value, danger = false }: { label: string; value: string; danger?: boolean }) {
  return (
    <div style={{ textAlign: 'right' }}>
      <div
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 15,
          color: danger ? 'var(--danger)' : 'var(--fg-default)',
        }}
      >
        {value}
      </div>
      <div style={{ fontSize: 11, color: 'var(--fg-subtle)' }}>{label}</div>
    </div>
  );
}
